import { Box, Flex, useToken } from '@chakra-ui/react';
import {
  Heading,
  HStack,
  Text,
  VStack,
} from '@ayble-health/dashboard-components';
import { Button } from 'components/ui/button';
import { Info } from 'lucide-react';
import { FoodCard } from './FoodCard';
import React, { useEffect, useState } from 'react';
import { IconButton } from '../../Form/IconButton';
import { PencilIcon } from '../../../icons/PencilIcon';
import { useParams } from 'react-router-dom';
import { Loader } from '../../Loader';
import { useUpdateSuspectedFoods } from '../../../api/healthCoach/mutations/useUpdateSuspectedFoods';
import { Food } from '../../../api/healthCoach/useSuspectedFoods';
import { getImage } from './util';

const heatLevelMap = {
  unlikely: 1,
  low: 2,
  medium: 3,
  high: 4,
};

export const SuspectedFoods = ({
  error = '',
  isLoading = false,
  suspectedFoods = [],
}: {
  error: string;
  isLoading: boolean;
  suspectedFoods: Food[];
}) => {
  const { patient } = useParams();
  const { mutation } = useUpdateSuspectedFoods(patient ?? '');
  const [isEditing, setIsEditing] = useState(false);
  const [selectedFoods, setSelectedFoods] = useState<string[]>([]);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const canAddFood = selectedFoods.length < 5;

  const suspectedFoodNames = suspectedFoods
    .filter((f) => f.potential_trigger)
    .map((f) => f.name);

  const handleFoodSelection = (foodId: string, checked: boolean) => {
    if (checked && canAddFood) {
      return setSelectedFoods([...selectedFoods, foodId]);
    }

    if (checked && !canAddFood) {
      setErrorMsg('You can only select 5 foods.');
      return;
    }

    const unCheckedFoodIndex = selectedFoods.indexOf(foodId);
    return setSelectedFoods([
      ...selectedFoods.slice(0, unCheckedFoodIndex),
      ...selectedFoods.slice(unCheckedFoodIndex + 1),
    ]);
  };

  useEffect(() => {
    if (!isLoading && !!error) {
      setErrorMsg(
        'Error while loading the suspected foods. Please try again later ot contact support.'
      );
    }
  }, [error, isLoading]);

  useEffect(() => {
    // reset error message
    if (!error && !!errorMsg && selectedFoods.length < 5) setErrorMsg('');
  }, [errorMsg, error, selectedFoods.length]);

  const [info600] = useToken('colors', ['info.600']);

  if (isLoading) {
    return (
      <Box p={0}>
        <Loader loading={true} />
      </Box>
    );
  }

  return (
    <VStack>
      <Flex alignItems={'center'} flexDir={'row'}>
        <Heading size={'md'} fontWeight={'normal'} textAlign="left">
          Suspect Foods
        </Heading>

        {!isEditing && (
          <IconButton
            onClick={() => {
              // Preselect foods already marked as suspected (matches FoodCard foodId = name).
              setSelectedFoods(suspectedFoodNames);
              setIsEditing(true);
            }}
          >
            <PencilIcon />
          </IconButton>
        )}

        {isEditing && (
          <HStack h={'24px'} alignItems={'center'}>
            <Button
              disabled={mutation.isLoading}
              h={'20px'}
              minH={'16px'}
              ml={2}
              onClick={() => {
                setIsEditing(false);
                setSelectedFoods([]);
              }}
              rounded={3}
              size="xs"
              variant={'outline'}
              w={'16px'}
            >
              Cancel
            </Button>

            <Button
              disabled={
                mutation.isLoading ||
                selectedFoods.length < 1 ||
                selectedFoods.length > 5
              }
              h={'20px'}
              loading={mutation.isLoading}
              minH={'16px'}
              ml={2}
              onClick={async () => {
                await mutation.mutateAsync(selectedFoods);
                setIsEditing(false);
              }}
              rounded={3}
              size="xs"
              w={'16px'}
            >
              Save
            </Button>
          </HStack>
        )}
      </Flex>

      <Box h={'32px'}>
        {isEditing && !errorMsg && canAddFood && (
          <HStack alignItems={'center'} pt={1}>
            <Info color={info600} size={16} style={{ marginRight: 4 }} />
            <Text>Select 1 to 5 foods to save</Text>
          </HStack>
        )}

        {errorMsg && <Text color={'red.500'}>{errorMsg}</Text>}
      </Box>

      <Box>
        <Flex flexDir={'row'} overflow={'scroll'} pb={3}>
          {suspectedFoods?.map(
            ({ heat, image_url, name, potential_trigger }, index) => (
              <FoodCard
                key={index}
                active={potential_trigger}
                editing={isEditing}
                foodId={name}
                foodLabel={name}
                imgURL={getImage(image_url)}
                onChange={handleFoodSelection}
                selected={selectedFoods.includes(name)}
                severityLabel={heat}
                severityLevel={heatLevelMap[heat]}
              />
            )
          )}
        </Flex>

        <HStack alignItems={'center'}>
          <Text fontStyle={'italic'}>
            From left to right, discover the highest-scoring food to the lowest
            →
          </Text>
        </HStack>
      </Box>
    </VStack>
  );
};
